import { Component, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormArray } from '@angular/forms';
import { StepsModule } from 'primeng/steps';
import { MenuItem, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { EducationInfoComponent } from './sections/education-info/education-info.component';
import { TestScoresComponent } from './sections/test-scores/test-scores.component';
import { RecommendationsComponent } from './sections/recommendations/recommendations.component';
import { ExperiencesComponent } from './sections/experiences/experiences.component';
import { AwardsAccoladesComponent } from './sections/awards-accolades/awards-accolades.component';
import { OnboardingLandingComponent } from './onboarding-landing/onboarding-landing.component';

@Component({
  selector: 'app-application-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepsModule,
      ButtonModule,
      TooltipModule,
    ToastModule,
    EducationInfoComponent,
    TestScoresComponent,
    RecommendationsComponent,
    ExperiencesComponent,
    AwardsAccoladesComponent,
    OnboardingLandingComponent
  ],
  providers: [MessageService],
  templateUrl: './application-form.component.html',
  styleUrls: ['./application-form.component.scss']
})
export class ApplicationFormComponent implements OnInit, AfterViewInit {
  @ViewChild(EducationInfoComponent) educationComp!: EducationInfoComponent;
  @ViewChild(TestScoresComponent) testScoresComp!: TestScoresComponent;
  @ViewChild(RecommendationsComponent) recommendationsComp!: RecommendationsComponent;
  @ViewChild(ExperiencesComponent) experiencesComp!: ExperiencesComponent;
  @ViewChild(AwardsAccoladesComponent) awardsComp!: AwardsAccoladesComponent;

  onboardingForm!: FormGroup;
  items: MenuItem[] = [];
  activeIndex: number = 0;
  isNextDisabled: boolean = true;
  // Steps that have their own Save/Update controls and therefore should show 'Next'
  stepsWithSeparateSave: number[] = [0, 2, 3, 4];
  mode: 'landing' | 'manual' | 'parse' = 'manual';
  parsedFiles: File[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) { }

  ngOnInit() {
    this.items = [
      { label: 'Education', title: 'Education', subtitle: 'Degrees & institutions' },
      { label: 'Test Scores', title: 'Test Scores', subtitle: 'SAT, IELTS, GRE..' },
      { label: 'Recommendations', title: 'Recommendations', subtitle: 'LOR contacts' },
      { label: 'Experiences', title: 'Experience', subtitle: 'Work & research history' },
      { label: 'Awards & Accolades', title: 'Awards & Accolades', subtitle: 'Achievements' }
    ];

    this.onboardingForm = this.fb.group({
      educationInfo: this.fb.group({}),
      testScores: this.fb.group({}),
      recommendations: this.fb.array([]),
      experiences: this.fb.array([]),
      awards: this.fb.group({})
    });
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  onModeSelected(mode: 'parse' | 'manual') {
    this.mode = mode;
  }

  onFilesUploaded(files: File[]) {
    this.parsedFiles = files;
  }

  nextStep() {
    // Synchronously force the active child component to update status
    if (this.activeIndex === 0 && this.educationComp) {
      this.educationComp.checkStepStatus();
    } else if (this.activeIndex === 2 && this.recommendationsComp) {
      this.recommendationsComp.checkStepStatus();
    } else if (this.activeIndex === 3 && this.experiencesComp) {
      this.experiencesComp.checkStepStatus();
    } else if (this.activeIndex === 4 && this.awardsComp) {
      this.awardsComp.checkStepStatus();
    }

    // Only proceed if the Next/Continue button is enabled. Saving is handled by each section's Save/Update button.
    if (this.isNextDisabled) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Unsaved Changes',
        detail: 'Please save all sections before proceeding.'
      });
      return;
    }

    if (this.activeIndex === 1) {
      this.testScoresComp.saveData();
      return;
    }

    this.proceed();
  }

  proceed() {
    if (this.activeIndex < this.items.length - 1) {
      this.activeIndex++;
      this.isNextDisabled = (this.activeIndex !== 1);
    } else {
      this.submitForm();
    }
  }

  prevStep() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
      this.isNextDisabled = (this.activeIndex !== 1);
    }
  }

  isStepValid(): boolean {
    const sections = ['educationInfo', 'testScores', 'recommendations', 'experiences', 'awards'];
    const currentSectionName = sections[this.activeIndex];
    const control = this.onboardingForm.get(currentSectionName);

    if (!control) return true;

    control.markAllAsTouched();

    if (!control.valid) {
      this.logErrorsRecursive(control, currentSectionName);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields.',
        life: 5000
      });
    }

    return control.valid;
  }

  logErrorsRecursive(control: any, name: string) {
    if (control.errors) {
      console.warn(`[ERROR] Field "${name}":`, control.errors);
    }
    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(key => {
        this.logErrorsRecursive(control.get(key), `${name}.${key}`);
      });
    } else if (control instanceof FormArray) {
      control.controls.forEach((c, i) => {
        this.logErrorsRecursive(c, `${name}[${i}]`);
      });
    }
  }

  submitForm() {
    if (this.onboardingForm.valid) {
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Onboarding completed successfully!' });
      setTimeout(() => {
        this.router.navigate(['/portal/dashboard'], { queryParams: { appFormSuccess: 'true' } });
      }, 1500);
    } else {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Form is invalid. Please check all sections.' });
    }
  }

  onStepSaved() {
    this.proceed();
  }

  getFooterLabel(): string {
    return this.stepsWithSeparateSave.includes(this.activeIndex) ? 'Next' : 'Continue';
  }

  onStepFailed(msg?: string) {
    this.messageService.add({
      severity: 'error',
      summary: 'Save Failed',
      detail: msg || 'Could not save data. Please try again.'
    });
  }
}
